import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { Link } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import {
  recordCertScan,
  saveScannedItem,
} from "../../services/collectorApi";
import {
  getCollectorToken,
  isCollectorEnabled,
  setCollectorToken,
} from "../../helpers/collectorSession";

import OwnershipClaimCard from "./OwnershipClaimCard";
import { collectorRecordKey } from "../../helpers/collectorIds";

import "./my-collection.scss";

type Props = {
  productUniqueId: string;
};

const scanKey = (productUniqueId: string) => `mc_cert_scan_${productUniqueId}`;
const itemKey = (productUniqueId: string) => `mc_cert_item_${productUniqueId}`;

/** The collector's entry for this piece, enough to offer the ownership claim on the certificate page. */
type SavedItem = { id: string; status?: string; itemType?: string; isSampleItem?: boolean };

const toSavedItem = (raw: any): SavedItem | null => {
  const id = raw ? collectorRecordKey(raw) : "";
  return id ? { id, status: raw.status, itemType: raw.itemType, isSampleItem: raw.isSampleItem } : null;
};

const readSavedItem = (productUniqueId: string): SavedItem | null => {
  try {
    return JSON.parse(sessionStorage.getItem(itemKey(productUniqueId)) || "null");
  } catch {
    return null;
  }
};

const SaveToCollectionButton: React.FC<Props> = ({ productUniqueId }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [item, setItem] = useState<SavedItem | null>(null);
  const enabled = isCollectorEnabled() && Boolean(productUniqueId);

  useEffect(() => {
    if (!enabled || !productUniqueId) {
      return;
    }
    const certToken = localStorage.getItem("productVerificationToken");
    if (!certToken) {
      return;
    }
    if (sessionStorage.getItem(scanKey(productUniqueId)) === "1") {
      setSaved(true);
      setItem(readSavedItem(productUniqueId));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await recordCertScan(certToken, productUniqueId);
        const token = res.data?.data?.token;
        if (token) {
          setCollectorToken(token, res.data?.data?.refreshToken);
        }
        if (res.data?.data?.item) {
          sessionStorage.setItem(scanKey(productUniqueId), "1");
          remember(toSavedItem(res.data.data.item), cancelled);
          if (!cancelled) {
            setSaved(true);
          }
        }
      } catch {
        // Certificate page must still render if collector enrol fails.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, productUniqueId]);

  function remember(next: SavedItem | null, cancelled = false) {
    if (!next) return;
    sessionStorage.setItem(itemKey(productUniqueId), JSON.stringify(next));
    if (!cancelled) setItem(next);
  }

  if (!enabled) {
    return null;
  }

  const handleSave = async () => {
    if (saved || saving) return;
    setSaving(true);
    try {
      const certToken = localStorage.getItem("productVerificationToken");
      if (certToken) {
        const res = await recordCertScan(certToken, productUniqueId);
        const token = res.data?.data?.token;
        if (token) {
          setCollectorToken(token, res.data?.data?.refreshToken);
        }
        if (res.data?.data?.item) {
          remember(toSavedItem(res.data.data.item));
        } else if (getCollectorToken()) {
          remember(toSavedItem((await saveScannedItem(productUniqueId)).data?.data));
        }
      } else if (getCollectorToken()) {
        remember(toSavedItem((await saveScannedItem(productUniqueId)).data?.data));
      } else {
        notifications({
          title: "Sign in required",
          message: "Verify your phone on the certificate page first",
          type: "warning",
        });
        return;
      }
      sessionStorage.setItem(scanKey(productUniqueId), "1");
      setSaved(true);
      notifications({
        title: "Saved!",
        message: "Added to Scanned",
        type: "success",
      });
    } catch (e: any) {
      const msg = e?.response?.data?.message || "Could not save to My Collection";
      notifications({ title: "Error", message: msg, type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="text-center py-4 my-3 border-top">
      <Button
        className="mc-btn-primary"
        onClick={handleSave}
        disabled={saving || saved}
      >
        {saved ? "Saved to Scanned" : saving ? "Saving…" : "Save to My Collection"}
      </Button>
      {saved && item?.status === "OWNED" && (
        <p className="small mt-2 mb-0">
          This piece is in your collection as yours.{" "}
          <Link to={`/my-collection/item/${item.id}`} className="mc-website-link">Open it</Link>
        </p>
      )}
      {saved && item?.status !== "OWNED" && (
        <p className="small mt-2 mb-0">
          <Link to="/my-collection/scanned" className="mc-website-link">Open Scanned</Link>
        </p>
      )}
      {saved && item && item.status === "SCANNED" && item.itemType === "HKP" && !item.isSampleItem && (
        <div className="text-start mx-auto mt-3" style={{ maxWidth: 520 }}>
          <OwnershipClaimCard itemId={item.id} onOwned={() => remember({ ...item, status: "OWNED" })} />
        </div>
      )}
    </div>
  );
};

export default SaveToCollectionButton;
